import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState, AppDispatch } from '../redux/store';
import { searchPlans, clearAllMessages } from '../redux/actions/planActions';
import { forceResetSearchResults } from '../redux/slices/planSlice';
import styles from './PlanSearch.module.css';
import ReturnToDashboard from './ReturnToDashboard';

const PlanSearch: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { searchResults, loading, error } = useSelector((state: RootState) => state.plan);
  const [employerName, setEmployerName] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [brokerName, setBrokerName] = useState('');
  const [brokerID, setBrokerID] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 5;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEmployerName(name === 'employerName' ? value : employerName);
    setCouponCode(name === 'couponCode' ? value : couponCode);
    setBrokerName(name === 'brokerName' ? value : brokerName);
    setBrokerID(name === 'brokerID' ? value : brokerID);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const searchParams = {
      employerName: employerName || undefined,
      planCode: couponCode || undefined,
      brokerName: brokerName || undefined,
      brokerID: brokerID || undefined
    };
    console.log('Dispatching searchPlans with params:', searchParams);
    dispatch(searchPlans(searchParams));
    setCurrentPage(1);
  };

  const handleClear = () => {
    setEmployerName('');
    setCouponCode('');
    setBrokerName('');
    setBrokerID('');
  };

  // Calculate pagination values
  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const currentResults = searchResults.slice(indexOfFirstResult, indexOfLastResult);
  const totalPages = Math.ceil(searchResults.length / resultsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Clear search results when the component mounts
  useEffect(() => {
    console.log('PlanSearch component mounted, forcefully resetting search results');
    dispatch(forceResetSearchResults());
    dispatch(clearAllMessages());
  }, [dispatch]);


  return (
    <div className={styles.planSearch}>
      <ReturnToDashboard />
      <div className={styles.header}>
        <h1>Search Plans</h1>
      </div>
      <div className={styles.searchContainer}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <div className={styles.inputGroup}>
            <label htmlFor="employerName">Employer Name</label>
            <input
              id="employerName"
              name="employerName"
              type="text"
              value={employerName}
              onChange={handleInputChange}
              placeholder="Enter employer name"
              className={styles.searchInput}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="couponCode">Coupon Code</label>
            <input
              id="couponCode"
              name="couponCode"
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Enter coupon code"
              className={styles.searchInput}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="brokerName">Broker Name</label>
            <input
              id="brokerName"
              name="brokerName"
              type="text"
              value={brokerName}
              onChange={handleInputChange}
              placeholder="Enter broker name"
              className={styles.searchInput}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="brokerID">Broker ID</label>
            <input
              id="brokerID"
              name="brokerID"
              type="text"
              value={brokerID}
              onChange={handleInputChange}
              placeholder="Enter broker ID"
              className={styles.searchInput}
            />
          </div>
          <div className={styles.buttonGroup}>
            <button type="submit" className={styles.searchButton}>Search</button>
            <button type="button" onClick={handleClear} className={styles.clearButton}>Clear</button>
          </div>
        </form>
      </div>
      {loading && <div className={styles.loading}>Loading...</div>}
      {error && <div className={styles.error}>{error.toString()}</div>}
      {!loading && !error && searchResults.length > 0 && (
        <div className={styles.tableContainer}>
          <table className={styles.resultsTable}>
            <thead>
              <tr>
                <th>Coupon Code</th>
                <th>Broker ID</th>
                <th>Broker Name</th>
                <th>Employer Name</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              {currentResults.map((result, index) => (
                <tr key={result.id || index}>
                  <td>{result.planCode || 'N/A'}</td>
                  <td>{result.brokerID || 'N/A'}</td>
                  <td>{result.brokerName || 'N/A'}</td>
                  <td>{result.employerName || 'N/A'}</td>
                  <td>{result.price || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {currentResults.length === 0 && <p>No results found.</p>}
          {searchResults.length > resultsPerPage && (
            <div className={styles.pagination}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={currentPage === number ? styles.activePage : ''}
                >
                  {number}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanSearch;